import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useAreas, useShops, type ShopRow, type AreaRow } from "@/hooks/useShops";

/**
 * 現在ヘッダーで選択中の地域/店舗を保持する共有コンテキスト。
 *
 * 仕様:
 *  - 「全地域」「全店舗」は存在しない。常に具体的な地域・店舗が選択された状態にする。
 *  - 地域を選ぶと店舗リストがその地域でフィルタされる（選択中店舗が地域外なら先頭店舗に補正）。
 *  - 店舗を変えると、各画面のデータ取得が選択中店舗基準で取り直される（useScopedAuth 経由）。
 *  - admin/root のみ店舗を切替可能。shop/user は自店固定（canSwitch=false）。
 */
interface CurrentShopContextValue {
  areas: AreaRow[];
  shops: ShopRow[];
  /** 選択中地域でフィルタした店舗リスト */
  filteredShops: ShopRow[];
  selectedAreaId: string | null;
  selectedShopId: string | null;
  setSelectedAreaId: (areaId: string) => void;
  setSelectedShopId: (shopId: string) => void;
  /** 店舗を切り替えられるロールか（admin/root のみ true） */
  canSwitch: boolean;
}

const CurrentShopContext = createContext<CurrentShopContextValue | undefined>(undefined);

const STORAGE_KEY = "sattou.selectedShopId";

export function CurrentShopProvider({ children }: { children: ReactNode }) {
  const { authUser } = useAuth();
  const { data: areas = [] } = useAreas();
  const { data: shops = [] } = useShops();

  const canSwitch = authUser?.role === "admin" || authUser?.role === "root";

  const [selectedAreaId, setSelectedAreaIdState] = useState<string | null>(null);
  const [selectedShopId, setSelectedShopIdState] = useState<string | null>(null);

  // 初期選択の確定（shops ロード後）。既に有効な選択があれば触らない。
  useEffect(() => {
    if (shops.length === 0) return;
    if (selectedShopId && shops.some((s) => s.id === selectedShopId)) return;

    let initial: string | null = null;
    if (!canSwitch) {
      // shop/user: 自店固定
      initial = authUser?.shop_id ?? null;
    } else {
      // admin/root: localStorage の前回選択 → 自分の所属店舗 → 先頭店舗
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && shops.some((s) => s.id === stored)) initial = stored;
      else if (authUser?.shop_id && shops.some((s) => s.id === authUser.shop_id)) initial = authUser.shop_id;
      else initial = shops[0].id;
    }

    if (initial) {
      setSelectedShopIdState(initial);
      const sh = shops.find((s) => s.id === initial);
      setSelectedAreaIdState(sh?.area_id ?? null);
    }
  }, [shops, canSwitch, authUser?.shop_id, selectedShopId]);

  const setSelectedShopId = (shopId: string) => {
    setSelectedShopIdState(shopId);
    const sh = shops.find((s) => s.id === shopId);
    if (sh?.area_id) setSelectedAreaIdState(sh.area_id);
    if (canSwitch) localStorage.setItem(STORAGE_KEY, shopId);
  };

  const setSelectedAreaId = (areaId: string) => {
    setSelectedAreaIdState(areaId);
    // 選択中店舗がこの地域に属さなければ、地域内の先頭店舗へ補正
    const inArea = shops.filter((s) => s.area_id === areaId);
    if (inArea.length > 0 && !inArea.some((s) => s.id === selectedShopId)) {
      setSelectedShopId(inArea[0].id);
    }
  };

  const filteredShops = useMemo(
    () => (selectedAreaId ? shops.filter((s) => s.area_id === selectedAreaId) : shops),
    [shops, selectedAreaId],
  );

  const value = useMemo<CurrentShopContextValue>(
    () => ({
      areas,
      shops,
      filteredShops,
      selectedAreaId,
      selectedShopId,
      setSelectedAreaId,
      setSelectedShopId,
      canSwitch,
    }),
    // setters は安定（クロージャ更新は state 経由）。依存は描画に必要な値のみ。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areas, shops, filteredShops, selectedAreaId, selectedShopId, canSwitch],
  );

  return <CurrentShopContext.Provider value={value}>{children}</CurrentShopContext.Provider>;
}

export function useCurrentShop(): CurrentShopContextValue {
  const ctx = useContext(CurrentShopContext);
  if (!ctx) throw new Error("useCurrentShop must be used within CurrentShopProvider");
  return ctx;
}

/** 現在のデータ取得対象として使うべき店舗ID（選択中店舗。未確定時は authUser.shop_id にフォールバック） */
export function useEffectiveShopId(): string | null {
  const ctx = useContext(CurrentShopContext);
  const { authUser } = useAuth();
  return ctx?.selectedShopId ?? authUser?.shop_id ?? null;
}

/**
 * useAuth() と同じ形を返すが、authUser.shop_id を「選択中店舗」に差し替える。
 * 各データフックは useAuth() の代わりにこれを使うだけで、フィルタ/queryKey/新規行の
 * shop_id が選択中店舗基準になり、店舗切替で自動的に再取得される。
 * Provider 外（公開ページ等）で呼ばれた場合は素の useAuth() にフォールバックする。
 */
export function useScopedAuth(): ReturnType<typeof useAuth> {
  const auth = useAuth();
  const ctx = useContext(CurrentShopContext);
  if (!ctx || !auth.authUser) return auth;
  const effectiveShopId = ctx.selectedShopId ?? auth.authUser.shop_id;
  if (effectiveShopId === auth.authUser.shop_id) return auth;
  return { ...auth, authUser: { ...auth.authUser, shop_id: effectiveShopId } };
}
